import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Trans, useLingui } from "@lingui/react/macro";
import axios from "../../utils/axios";

interface ApiKeyConnectDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  integrationIdentifier: string;
  integrationName: string;
  helpText: string;
  helpUrl?: string;
}

const ApiKeyConnectDialog: React.FC<ApiKeyConnectDialogProps> = ({
  open,
  onClose,
  onSuccess,
  integrationIdentifier,
  integrationName,
  helpText,
  helpUrl,
}) => {
  const theme = useTheme();
  const { t } = useLingui();
  const [apiKey, setApiKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!apiKey.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(
        "/v1/integrations/create-integration/",
        JSON.stringify({
          identifier: integrationIdentifier,
          secret: apiKey.trim(),
        }),
        { withCredentials: true },
      );
      setApiKey("");
      onSuccess();
    } catch (err: any) {
      console.error(`Failed to connect ${integrationName}:`, err);
      setError(
        err.response?.data?.message ||
          t`Failed to connect. Please check your API key and try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setApiKey("");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(2),
          paddingBottom: theme.spacing(2),
        },
      }}
    >
      <DialogContent>
        <DialogTitle
          sx={{
            color: theme.palette.primary.main,
            paddingLeft: 0,
            paddingTop: 0,
          }}
        >
          <Trans context="Dialog title for connecting an integration via API key">
            Connect to {integrationName}
          </Trans>
        </DialogTitle>

        <DialogContentText sx={{ marginBottom: theme.spacing(2) }}>
          {helpText}
        </DialogContentText>

        {helpUrl && (
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <Link
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: "bold" }}
            >
              <Trans context="Link to open the integration's settings page">
                Open {integrationName} Settings →
              </Trans>
            </Link>
          </Box>
        )}

        <TextField
          label={<Trans context="Label for API key input field">API Key</Trans>}
          fullWidth
          variant="filled"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          disabled={isSubmitting}
          type="password"
          InputProps={{
            style: {
              borderRadius: theme.spacing(2),
            },
            disableUnderline: true,
          }}
        />

        {error && (
          <Typography
            color="error"
            variant="body2"
            sx={{ marginTop: theme.spacing(2) }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ marginRight: theme.spacing(2) }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ borderRadius: theme.spacing(2) }}
        >
          <Trans context="Button to cancel and close the dialog">Cancel</Trans>
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !apiKey.trim()}
          sx={{ borderRadius: theme.spacing(2) }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Trans context="Button to submit credentials and connect">
              Connect
            </Trans>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyConnectDialog;
